import {useEffect, useRef} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";

import {ackDirectMessageNotificationsForThread} from "@/src/modules/notification-feed/lib/direct-message-notification-read";

import {
  invalidateNotificationFeed,
  markThreadAsReadInListCaches,
  refetchAllDirectMessageThreadLists,
} from "../lib/direct-message-query-cache";
import {directMessagesService} from "../services";

export const DIRECT_MESSAGE_THREAD_QUERY_KEY = "directmessage-thread";

export default function useDirectMessageThread(threadId: number | null, enabled = true) {
  const queryClient = useQueryClient();
  const syncedFeedForOpenRef = useRef<number | null>(null);

  const {data, isPending, isFetching, isError, error, refetch} = useQuery({
    queryKey: [DIRECT_MESSAGE_THREAD_QUERY_KEY, threadId],
    queryFn: () => directMessagesService.getThread(threadId!),
    enabled: enabled && threadId != null,
    retry: false,
    networkMode: "offlineFirst",
  });

  useEffect(() => {
    if (!enabled || threadId == null) {
      syncedFeedForOpenRef.current = null;
      return;
    }

    if (!data || syncedFeedForOpenRef.current === threadId) {
      return;
    }

    syncedFeedForOpenRef.current = threadId;
    markThreadAsReadInListCaches(queryClient, threadId);
    void ackDirectMessageNotificationsForThread(threadId, queryClient);
    invalidateNotificationFeed(queryClient);
    refetchAllDirectMessageThreadLists(queryClient);
  }, [data, enabled, queryClient, threadId]);

  return {
    detail: data,
    isPending,
    isFetching,
    isError,
    error,
    refetch,
  };
}
